import * as React from "react";

import type { OrgLlmModel } from "@/shared/domain/orgs";
import {
  useBedrockCatalog,
  useOrgLlmProvider,
  useOrgLlmProviderTemplates,
  useResetOrgLlmProvider,
} from "@/shared/domain/orgs/hooks";
import {
  describeModelSource,
  isRobotoDefaultModels,
  isRobotoManaged,
} from "@/shared/domain/orgs/orgModelConfig";
import {
  Badge,
  ConfirmDialog,
  FormSection,
  RobotoButton,
  Spinner,
} from "@/shared/foundation/primitives";
import { userFacingErrorMessage } from "@/shared/http/userFacingErrorMessage";

import { ModelListEditor } from "./ModelListEditor";
import { ModelTable } from "./ModelTable";
import styles from "./OrgAiModels.module.css";
import { robotoModelChoices } from "./robotoModelChoices";

interface OrgAiModelsSectionProps {
  orgId: string;
  /** Org admins of this org and Roboto admins may change it; everyone else reads. */
  canEdit: boolean;
  /**
   * Roboto admins add Roboto models from the Bedrock catalog (those offered
   * to new orgs or already on this org); org admins from the org's template.
   */
  isRobotoAdmin: boolean;
  /** Only premium orgs may add models on their own connections, or run them. */
  isPremium: boolean;
  /**
   * Whether the org may use its own LLM connections (a feature flag). Off,
   * own models already on the list show as unavailable and can only be
   * switched or removed, and the utility model is not shown at all.
   */
  ownConnectionsEnabled: boolean;
}

/**
 * The org's chat model list: which models members can pick, where each runs
 * (Roboto or one of the org's own connections), the default and, with own
 * connections enabled, the utility model, as a table. Org admins and Roboto
 * admins change it row by row, each change saved at once; everyone else reads.
 */
export function OrgAiModelsSection({
  orgId,
  canEdit,
  isRobotoAdmin,
  isPremium,
  ownConnectionsEnabled,
}: OrgAiModelsSectionProps) {
  const providerQuery = useOrgLlmProvider(orgId);
  const templatesQuery = useOrgLlmProviderTemplates(orgId);
  const catalogQuery = useBedrockCatalog({ enabled: isRobotoAdmin });
  const resetMutation = useResetOrgLlmProvider();
  const [confirmingReset, setConfirmingReset] = React.useState(false);

  const catalog = catalogQuery.data;

  // The Roboto default, to tell "Roboto default" from another selection. The
  // catalog is exact; without it (org admins and members) the template's
  // Roboto models are the closest answer.
  const robotoDefault = React.useMemo(
    () =>
      catalog?.filter((m) => m.in_roboto_default) ??
      templatesQuery.data?.roboto_models,
    [catalog, templatesQuery.data],
  );

  // Provider model ids of Roboto models, known only from the catalog.
  const robotoModelIds = React.useMemo(
    () =>
      catalog === undefined
        ? undefined
        : new Map(catalog.map((m) => [m.id, m.model_id])),
    [catalog],
  );

  // Provider model id behind Roboto's utility model: the catalog entry bound
  // to the utility role. Known only to Roboto admins, who see the catalog.
  const robotoUtilityModelId = catalog?.find((m) =>
    m.bound_roles.includes("utility"),
  )?.model_id;

  if (providerQuery.isLoading) {
    return (
      <FormSection title="AI models">
        <Spinner aria-label="Loading AI models" />
      </FormSection>
    );
  }

  if (providerQuery.data === undefined) {
    return (
      <FormSection title="AI models">
        <p className={styles.error} role="alert">
          {userFacingErrorMessage(providerQuery.error)}
        </p>
      </FormSection>
    );
  }

  const { config, connections } = providerQuery.data;
  const hasOwnModels = config.models.some((m) => !isRobotoManaged(m));
  const isRobotoDefault =
    robotoDefault !== undefined &&
    isRobotoDefaultModels(config.models, robotoDefault);

  const reset = () =>
    resetMutation.mutate(
      { orgId },
      { onSuccess: () => setConfirmingReset(false) },
    );

  const actions =
    canEdit && !isRobotoDefault ? (
      <RobotoButton
        variant="secondary"
        size="sm"
        loading={resetMutation.isPending && !confirmingReset}
        onClick={hasOwnModels ? () => setConfirmingReset(true) : reset}
      >
        Use Roboto default
      </RobotoButton>
    ) : undefined;

  return (
    <FormSection
      title="AI models"
      hint="The models your members can chat with."
      actions={actions}
    >
      <div className={styles.section}>
        {resetMutation.isError && !confirmingReset && (
          <p className={styles.error} role="alert">
            {userFacingErrorMessage(resetMutation.error)}
          </p>
        )}
        <div className={styles.sourceRow}>
          <span className={styles.fieldLabel}>Source</span>
          <Badge variant="label">
            {describeModelSource(config.models, robotoDefault)}
          </Badge>
        </div>
        {canEdit ? (
          <ModelListEditor
            orgId={orgId}
            models={config.models}
            listModified={config.modified}
            connections={connections}
            isPremium={isPremium}
            ownConnectionsEnabled={ownConnectionsEnabled}
            robotoModelIds={robotoModelIds}
            robotoUtilityModelId={robotoUtilityModelId}
            robotoChoices={robotoModelChoices({
              catalog: isRobotoAdmin ? catalog : undefined,
              templateModels: templatesQuery.data?.roboto_models,
              savedModels: config.models,
            })}
          />
        ) : (
          <ModelTable
            models={config.models}
            connections={connections}
            isPremium={isPremium}
            ownConnectionsEnabled={ownConnectionsEnabled}
            robotoModelIds={robotoModelIds}
            robotoUtilityModelId={robotoUtilityModelId}
          />
        )}
      </div>
      <ConfirmDialog
        open={confirmingReset}
        onOpenChange={setConfirmingReset}
        title="Remove your own models?"
        description={`The list goes back to Roboto's default models, and ${ownModelNames(config.models)} come off it. Your connections are kept.`}
        confirmLabel="Use Roboto default"
        destructive
        pending={resetMutation.isPending}
        onConfirm={reset}
      >
        {resetMutation.isError && (
          <p className={styles.error} role="alert">
            {userFacingErrorMessage(resetMutation.error)}
          </p>
        )}
      </ConfirmDialog>
    </FormSection>
  );
}

function ownModelNames(models: readonly OrgLlmModel[]): string {
  return models
    .filter((m) => !isRobotoManaged(m))
    .map((m) => m.label)
    .join(", ");
}
